import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import * as Sentry from "@sentry/react";
import store from "./redux/store";
import { setupApi } from "./api/axiosInstance";
import App from "./App.jsx";
import "./index.scss";

/** ----- Sentry setup ----- */
const env =
  (import.meta.env?.VITE_SENTRY_ENV) ||
  import.meta.env?.MODE ||
  "development";

// Use Amplify-provided commit id wired via VITE_COMMIT_SHA=${AWS_COMMIT_ID}
const release =
  (import.meta.env?.VITE_COMMIT_SHA) ||
  (import.meta.env?.VITE_GIT_SHA) ||
  'local-dev';

// If you don’t want to add per-env sample rate vars, derive them here:
const isProd = env === "production";
const tracesSampleRate = isProd ? 0.2 : 1.0;          // APM tracing
const profilesSampleRate = isProd ? 0.2 : 1.0;        // CPU profiling
const replaysSessionSampleRate = isProd ? 0.1 : 0.0;  // session replays
const replaysOnErrorSampleRate = 1.0;                 // keep at 1.0

Sentry.init({
  dsn: import.meta.env?.VITE_SENTRY_DSN, 
  environment: env,
  release,

  attachStacktrace: true,
  sendDefaultPii: false, 
  beforeSend(event) {
    if (event.user) {
      delete event.user.ip_address;
    }
    return event;
  },

  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.browserProfilingIntegration(),
    Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate,
  profilesSampleRate,
  replaysSessionSampleRate,
  replaysOnErrorSampleRate,
  enableLogs: true,
});

// Configure axios interceptors before any route component mounts.
setupApi();


ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<div>Something went wrong.</div>}>
    <Provider store={store}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </Provider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((reg) => {
        console.log("Service worker registered:", reg);
      })
      .catch((err) => {
        console.error("Service worker registration failed:", err);
      });
  });
}
